/**
 * The flows behind the leave sheets: sign-out (upload, then clear — or the
 * leave sheet) and adopting a session (adopted, the switch sheet, or a
 * failure). Hooks only, so the sheets file stays components only.
 */

import { useCallback, useState } from "react";

import { LeaveSheet, SwitchAccountSheet } from "@/features/onboarding/leave_sheets";
import { type SignOutOutcome, useAuth } from "@/hooks/use_auth";
import type { Session } from "@/lib/backend";

/**
 * Sign-out, from any button: upload, then clear — or the leave sheet.
 *
 * Returns the trigger and the sheet to render (null when nothing is being
 * asked). `error` carries a sign-out that threw outright, which `endSession`
 * is built never to do, but which is reported rather than lost if it does.
 */
export function useSignOutFlow(): {
  begin: () => void;
  busy: boolean;
  error: string | null;
  sheet: React.JSX.Element | null;
} {
  const { signOut, requestReauth } = useAuth();
  const [blocked, setBlocked] = useState<Extract<SignOutOutcome, { kind: "blocked" }> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    (options: { discard?: boolean }) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      signOut(options)
        .then((outcome) => {
          setBlocked(outcome.kind === "blocked" ? outcome : null);
        })
        .catch((cause: unknown) => {
          setError(cause instanceof Error ? `Could not sign out: ${cause.message}` : "Could not sign out.");
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [busy, signOut],
  );

  const cancel = useCallback(() => {
    setBlocked(null);
  }, []);

  const sheet =
    blocked === null ? null : (
      <LeaveSheet
        pending={blocked.pending}
        reason={blocked.reason}
        busy={busy}
        onRetry={() => {
          run({});
        }}
        onSignInAgain={() => {
          setBlocked(null);
          requestReauth();
        }}
        onDiscard={() => {
          run({ discard: true });
        }}
        onCancel={cancel}
      />
    );

  return {
    begin: () => {
      run({});
    },
    busy,
    error,
    sheet,
  };
}

/**
 * Hand a fresh session to `applySession` and deal with every outcome: adopted,
 * blocked by another account's pending changes (the switch sheet), or failed.
 * Shared by the sign-in screen and the "Sign in again" sheet.
 */
export function useAdoptSession(onAdopted?: () => void): {
  adopt: (session: Session) => void;
  failure: string | null;
  clearFailure: () => void;
  sheet: React.JSX.Element | null;
} {
  const { applySession } = useAuth();
  const [failure, setFailure] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<{
    session: Session;
    pending: number;
    previousEmail: string | null;
  } | null>(null);

  const adopt = useCallback(
    (session: Session, options: { discard?: boolean } = {}) => {
      setFailure(null);
      // `applySession` reads and can clear the local database, so it can reject
      // — and an unreadable Dexie is precisely what can land a user here.
      // Without the catch the screen would sit on "Signing you in…" for ever.
      applySession(session, options)
        .then((outcome) => {
          if (outcome.kind === "blocked") {
            setBlocked({ session, pending: outcome.pending, previousEmail: outcome.previousEmail });
            return;
          }
          setBlocked(null);
          onAdopted?.();
        })
        .catch((cause: unknown) => {
          setBlocked(null);
          setFailure(
            cause instanceof Error
              ? `Could not finish signing in: ${cause.message}`
              : "Could not finish signing in.",
          );
        });
    },
    [applySession, onAdopted],
  );

  const keep = useCallback(() => {
    setBlocked(null);
    setFailure("Kept this device's existing ledger. Nothing was changed.");
  }, []);

  const sheet =
    blocked === null ? null : (
      <SwitchAccountSheet
        pending={blocked.pending}
        previousEmail={blocked.previousEmail}
        incomingEmail={blocked.session.email}
        onSignInAsPrevious={() => {
          const previous = blocked.previousEmail;
          setBlocked(null);
          setFailure(
            previous !== null && previous !== ""
              ? `Nothing was changed. Choose ${previous} when Google asks, and the changes will upload.`
              : "Nothing was changed. Choose the previous account when Google asks, and the changes will upload.",
          );
        }}
        onDiscard={() => {
          adopt(blocked.session, { discard: true });
        }}
        onCancel={keep}
      />
    );

  return {
    adopt: (session) => {
      adopt(session);
    },
    failure,
    clearFailure: () => {
      setFailure(null);
    },
    sheet,
  };
}
